"use client"

import { useIsMobile } from "@/ui/common/bit/MediaQueries"
import { extractBlockProperties } from "@/utils/simplifyData"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import ReactPlayer from "react-player"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { SwiperSlide } from "swiper/react"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import MediaGalleryCard from "./MediaGalleryCard/MediaGalleryCard"
import { StyledBlockWrapper, StyledSwiperMultiDevice } from "./styles"

// Styled components for mobile media items
const MobileMediaContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: ${sizes.borderRadius};
  overflow: hidden;
  background-color: ${colors.lightestGray};
`

const MobileVideoContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: ${sizes.borderRadius};
  overflow: hidden;
`

const StyledReactPlayer = styled(ReactPlayer)`
  border-radius: ${sizes.borderRadius};
  height: 100% !important;
  width: 100% !important;
`
const StyledDescription = styled(Typography)`
  padding-top: 24px;
  // text-align: left;
`
const StyledTitle = styled(Typography)`
  text-align: left;
`
const StyledMobileTextContainer = styled(Box)`
  display: flex;
  // justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${colors.black};
  ${mediaQueries.tablet} {
    display: none;
  }
`

interface ImageItem {
  url: string
  altText?: string
}

interface ImageGalleryBlockProps {
  data: any
  id?: string
}

const ImageGalleryBlock = ({ data, id }: ImageGalleryBlockProps) => {
  const { itemsList, title, text } = data
  const items = extractBlockProperties(itemsList.blocks)
  const isMobile = useIsMobile()

  // Extract string values from CMS objects
  const titleText = title?.value || title || ""
  const descriptionText = text?.value || text || ""

  // Extract all media items for mobile rendering
  const extractAllMediaItems = (items: any[]) => {
    const allMedia: Array<{
      type: "image" | "video"
      src: string
      alt?: string
      href?: string
    }> = []

    items.forEach((item: any) => {
      // Extract images
      if (item.rightImage) {
        allMedia.push({
          type: "image",
          src: item.rightImage.src,
          alt: item.rightImage.alt || "Gallery image",
        })
      }
      if (item.middleImage) {
        allMedia.push({
          type: "image",
          src: item.middleImage.src,
          alt: item.middleImage.alt || "Gallery image",
        })
      }
      if (item.upperLeftImage) {
        allMedia.push({
          type: "image",
          src: item.upperLeftImage.src,
          alt: item.upperLeftImage.alt || "Gallery image",
        })
      }
      if (item.bottomLeftImage) {
        allMedia.push({
          type: "image",
          src: item.bottomLeftImage.src,
          alt: item.bottomLeftImage.alt || "Gallery image",
        })
      }

      // Extract videos
      if (item.middleVideo && item.middleVideo.href) {
        allMedia.push({
          type: "video",
          src: item.middleVideo.href,
          href: item.middleVideo.href,
        })
      }
    })

    return allMedia
  }

  const allMediaItems = extractAllMediaItems(items)

  return (
    <StyledBlockWrapper id={id}>
      <StyledMobileTextContainer>
        <StyledTitle variant="h2" color="black">
          {titleText}
        </StyledTitle>
        <StyledDescription>{descriptionText}</StyledDescription>
      </StyledMobileTextContainer>
      <StyledSwiperMultiDevice
        swiperProps={{
          slidesPerView: 1,
          centeredSlides: false,
          spaceBetween: 16,
          pagination: isMobile ? { clickable: true } : false,
        }}
        desktopSwiperProps={{
          slidesPerView: 1,
          centeredSlides: false,
          loop: false,
          navigation: isMobile,
          spaceBetween: 0,
        }}
      >
        {isMobile
          ? // Mobile: Render all media items in swiper
            allMediaItems.map((mediaItem, index) => (
              <SwiperSlide key={`mobile-media-${index}`}>
                {mediaItem.type === "image" ? (
                  <MobileMediaContainer>
                    <Image
                      src={mediaItem.src}
                      alt={mediaItem.alt || "Gallery image"}
                      fill
                      style={{ objectFit: "cover" }}
                    />
                  </MobileMediaContainer>
                ) : (
                  <MobileVideoContainer>
                    <StyledReactPlayer
                      controls
                      url={mediaItem.href}
                      width="100%"
                      height="100%"
                    />
                  </MobileVideoContainer>
                )}
              </SwiperSlide>
            ))
          : // Desktop: Render MediaGalleryCard components
            items.map((item: any, index: number) => (
              <SwiperSlide key={`gallery-item-${index}`}>
                <MediaGalleryCard
                  item={item}
                  title={titleText}
                  text={descriptionText}
                />
              </SwiperSlide>
            ))}
      </StyledSwiperMultiDevice>
    </StyledBlockWrapper>
  )
}

export default ImageGalleryBlock
